import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, Send, User, BarChart3, Trophy, Share2, Download, Link, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ArticleComments - Comments section for articles
 */
export const ArticleComments = ({ articleId, title = "Fan Comments" }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');

  // Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/comments/${articleId}`);
      setComments(response.data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    setError('');

    try {
      await axios.post(
        `${BACKEND_URL}/api/comments`,
        { article_id: articleId, content: newComment.trim() },
        { withCredentials: true }
      );
      setNewComment('');
      await fetchComments();
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Please set a nickname in your profile first');
      } else {
        setError('Failed to post comment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="article-comments">
      <div className="bg-gradient-to-r from-[#00204E] to-[#0b4e9b] px-6 py-4">
        <h3 className="text-white font-bold text-lg flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          {title}
          <span className="text-blue-300 text-sm font-normal">({comments.length})</span>
        </h3>
      </div>

      {/* Comment Form */}
      <div className="p-4 border-b border-gray-100">
        {!user ? (
          <a 
            href="/login" 
            className="block text-center text-blue-600 hover:text-blue-700 font-medium py-3"
            data-testid="comments-login-prompt"
          >
            Sign in to join the conversation
          </a>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts on the game..."
              maxLength={1000}
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              disabled={submitting}
              data-testid="comment-input"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{newComment.length}/1000</span>
              <button
                type="submit"
                disabled={!newComment.trim() || submitting}
                className="bg-[#00204E] hover:bg-[#0b4e9b] disabled:bg-gray-300 text-white px-5 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
                data-testid="comment-submit"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </form>
        )}
      </div>

      {/* Comments List */}
      <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          comments.map((comment, idx) => (
            <div key={comment.comment_id || idx} className="p-4 hover:bg-gray-50" data-testid={`comment-${idx}`}>
              <div className="flex gap-3">
                {comment.user_picture ? (
                  <img src={comment.user_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[#00204E] text-sm">{comment.user_name || 'Fan'}</span>
                    <span className="text-xs text-gray-400">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">{comment.content}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * ScorePredictionWidget - Submit and view score predictions
 */
export const ScorePredictionWidget = ({ 
  articleId, 
  team1 = "Jackson State",
  team2 = "Alabama A&M",
  team1Logo = "https://a.espncdn.com/i/teamlogos/ncaa/500/2870.png",
  team2Logo = "https://a.espncdn.com/i/teamlogos/ncaa/500/2010.png"
}) => {
  const [predictions, setPredictions] = useState([]);
  const [stats, setStats] = useState({});
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Fetch predictions
  const fetchPredictions = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/score-predictions/${articleId}`);
      setPredictions(response.data.predictions || []);
      setStats(response.data.stats || {});
    } catch (err) {
      console.error('Error fetching predictions:', err);
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || team1Score === '' || team2Score === '') return;

    setSubmitting(true);
    setError('');

    try {
      await axios.post(
        `${BACKEND_URL}/api/score-predictions`,
        {
          article_id: articleId,
          team1_name: team1,
          team2_name: team2,
          team1_score: parseInt(team1Score),
          team2_score: parseInt(team2Score),
          comment: comment.trim() || null
        },
        { withCredentials: true }
      );
      setSubmitted(true);
      await fetchPredictions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit prediction');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="score-prediction-widget">
      <div className="bg-gradient-to-r from-[#0b4e9b] via-yellow-500 to-[#8e0e2c] px-6 py-4">
        <h3 className="text-white font-bold text-lg flex items-center gap-2">
          <Trophy className="w-5 h-5" />
          Predict the Score
        </h3>
        <p className="text-white/80 text-sm">What's your final score prediction?</p>
      </div>

      <div className="p-6">
        {/* Prediction Form */}
        {!user ? (
          <a href="/login" className="block text-center text-blue-600 hover:text-blue-700 font-medium py-4" data-testid="prediction-login-prompt">
            Sign in to submit your prediction
          </a>
        ) : submitted ? (
          <div className="text-center py-4 text-green-600">
            <Check className="w-8 h-8 mx-auto mb-2" />
            <p className="font-medium">Prediction submitted!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4 items-center">
              {/* Team 1 */}
              <div className="text-center">
                <img src={team1Logo} alt={team1} className="w-16 h-16 mx-auto mb-2 object-contain" />
                <p className="font-bold text-sm text-[#00204E]">{team1}</p>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={team1Score}
                  onChange={(e) => setTeam1Score(e.target.value)}
                  placeholder="0"
                  className="w-20 h-12 text-center text-2xl font-bold border-2 border-[#0b4e9b] rounded-lg mx-auto mt-2"
                  data-testid="team1-score-input"
                />
              </div>

              {/* VS */}
              <div className="text-center text-gray-400 font-bold text-xl">VS</div>

              {/* Team 2 */}
              <div className="text-center">
                <img src={team2Logo} alt={team2} className="w-16 h-16 mx-auto mb-2 object-contain" />
                <p className="font-bold text-sm text-[#8e0e2c]">{team2}</p>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={team2Score}
                  onChange={(e) => setTeam2Score(e.target.value)}
                  placeholder="0"
                  className="w-20 h-12 text-center text-2xl font-bold border-2 border-[#8e0e2c] rounded-lg mx-auto mt-2"
                  data-testid="team2-score-input"
                />
              </div>
            </div>

            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment (optional)"
              maxLength={200}
              className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm"
            />

            <button
              type="submit"
              disabled={team1Score === '' || team2Score === '' || submitting}
              className="w-full bg-gradient-to-r from-[#0b4e9b] to-[#8e0e2c] hover:opacity-90 disabled:opacity-50 text-white py-3 rounded-lg font-bold transition-opacity"
              data-testid="submit-prediction"
            >
              {submitting ? 'Submitting...' : 'Submit Prediction'}
            </button>
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          </form>
        )}

        {/* Stats */}
        {stats.total_predictions > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">{stats.total_predictions} predictions</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-sm">
              <div>
                <p className="font-bold text-[#0b4e9b]">{stats.avg_team1_score || 0}</p>
                <p className="text-xs text-gray-500">Avg {team1.split(' ')[0]}</p>
              </div>
              <div>
                <p className="font-bold text-gray-600">{stats.team1_win_pct}% - {stats.team2_win_pct}%</p>
                <p className="text-xs text-gray-500">Win picks</p>
              </div>
              <div>
                <p className="font-bold text-[#8e0e2c]">{stats.avg_team2_score || 0}</p>
                <p className="text-xs text-gray-500">Avg {team2.split(' ')[0]}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ShareWidget - Share article with downloadable graphic
 */
export const ShareWidget = ({ 
  articleId,
  articleTitle,
  articleUrl,
  team1 = "Jackson State",
  team2 = "Alabama A&M"
}) => {
  const [copied, setCopied] = useState(false);
  const fullUrl = `https://shelovesthee.com/${articleId}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = fullUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: articleTitle,
          text: `${team1} vs ${team2} - SheLovesThee Game of the Week`,
          url: fullUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="share-widget">
      <div className="bg-gradient-to-r from-[#00204E] to-[#0b4e9b] px-6 py-4">
        <h3 className="text-white font-bold text-lg flex items-center gap-2">
          <Share2 className="w-5 h-5" />
          Share This Article
        </h3>
      </div>

      <div className="p-6 space-y-4">
        <button
          onClick={handleShare}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          data-testid="share-button"
        >
          <Share2 className="w-5 h-5" />
          Share Article
        </button>

        <button
          onClick={handleCopyLink}
          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          data-testid="copy-link-button"
        >
          {copied ? (
            <>
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-green-600">Link Copied!</span>
            </>
          ) : (
            <>
              <Link className="w-5 h-5" />
              Copy Link
            </>
          )}
        </button>

        <div className="text-xs text-gray-500 text-center break-all">
          {fullUrl}
        </div>
      </div>
    </div>
  );
};

export default { ArticleComments, ScorePredictionWidget, ShareWidget };
